import type { HoverIdleSaveStorage } from "./hoverIdleStorage";
import type { HoverIdleSaveData } from "./hoverIdleSaveData";
import {
  readSavedAt,
  type OfflineRewardRepository,
  type SaveRepository,
} from "./saveRepository";

const IS_DEV = import.meta.env.DEV;

export interface SyncedSaveStorageOptions {
  readonly ownerId: string;
  readonly local: SaveRepository & OfflineRewardRepository;
  readonly cloud: SaveRepository | null;
}

export function createSyncedSaveStorage(
  options: SyncedSaveStorageOptions,
): HoverIdleSaveStorage {
  const { ownerId, local, cloud } = options;

  return {
    async loadLatest() {
      const localPromise = local.load(ownerId).catch((error) => {
        warnDev("local load failed", error);
        return null;
      });
      const cloudPromise = cloud
        ? cloud.load(ownerId).catch((error) => {
            warnDev("cloud load failed; falling back to local", error);
            return null;
          })
        : Promise.resolve(null);

      const [localRaw, cloudRaw] = await Promise.all([
        localPromise,
        cloudPromise,
      ]);

      if (localRaw === null && cloudRaw === null) {
        return null;
      }
      if (localRaw !== null && cloudRaw === null) {
        if (cloud !== null) {
          uploadIfSavable(cloud, ownerId, localRaw, "cloud backfill failed");
        }
        return localRaw;
      }
      if (localRaw === null && cloudRaw !== null) {
        await writeIfSavable(local, ownerId, cloudRaw, "local restore failed");
        return cloudRaw;
      }

      const localSavedAt = readSavedAt(localRaw) ?? -Infinity;
      const cloudSavedAt = readSavedAt(cloudRaw) ?? -Infinity;
      if (cloudSavedAt > localSavedAt) {
        await writeIfSavable(local, ownerId, cloudRaw, "local refresh failed");
        return cloudRaw;
      }
      return localRaw;
    },

    async save(saveData) {
      await local.save(ownerId, saveData);
      if (cloud !== null) {
        cloud.save(ownerId, saveData).catch((error) => {
          warnDev("cloud save failed; local save retained", error);
        });
      }
    },

    async commitOfflineReward(request) {
      const committed = await local.commitOfflineReward(ownerId, request);
      if (committed.ok && cloud !== null) {
        cloud.save(ownerId, committed.saveData).catch((error) => {
          warnDev(
            "cloud offline reward mirror failed; local commit retained",
            error,
          );
        });
      }
      return committed;
    },
  };
}

function uploadIfSavable(
  repo: SaveRepository,
  ownerId: string,
  raw: unknown,
  context: string,
): void {
  const saveData = asSaveData(raw);
  if (saveData === null) {
    return;
  }
  repo.save(ownerId, saveData).catch((error) => {
    warnDev(context, error);
  });
}

async function writeIfSavable(
  repo: SaveRepository,
  ownerId: string,
  raw: unknown,
  context: string,
): Promise<void> {
  const saveData = asSaveData(raw);
  if (saveData === null) {
    return;
  }
  await repo.save(ownerId, saveData).catch((error) => {
    warnDev(context, error);
  });
}

function asSaveData(raw: unknown): HoverIdleSaveData | null {
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  if (readSavedAt(raw) === null) {
    return null;
  }
  return raw as HoverIdleSaveData;
}

function warnDev(message: string, error: unknown): void {
  if (IS_DEV) {
    console.warn(`HoverIdle save sync: ${message}`, error);
  }
}
